import classNames from 'classnames';
import { extendTailwindMerge } from 'tailwind-merge';

import type { ArgumentArray } from 'classnames';

/**
 * The thirteen Design System 2.0 type utilities, one per Figma text style.
 *
 * Listed here as well as declared in `styles/design-system.css` because `tailwind-merge` carries
 * its own static map of Tailwind's class groups and has no way to read a project's `@utility`
 * blocks. Keep in step with the `@utility text-*` blocks in that file; the parity test in
 * `styles/__tests__/typographyTokens.test.ts` pins the stylesheet's side of the pair.
 */
const TYPE_UTILITIES = [
  'heading-xl',
  'heading-lg',
  'heading-md',
  'heading-sm',
  'heading-xs',
  'body-lg',
  'body-md',
  'body-sm',
  'body-xs',
  'label-lg',
  'label-md',
  'label-sm',
  'label-xs',
] as const;

/**
 * `twMerge`, taught that `text-heading-xs` and its twelve siblings are FONT SIZES.
 *
 * Unregistered, `tailwind-merge` reads any unknown `text-<word>` as a colour, and that is not a
 * cosmetic misfiling — it silently drops a real declaration. Measured: bare
 * `twMerge('text-body-md', 'text-[var(--ds-checkbox-label)]')` returns only the colour, because it
 * believes the two conflict. Several recipes set their type and their colour in the same class list
 * and are then passed through `cn()`, so the label on a checkbox would have lost its size, line
 * height and weight altogether — with no type error and no lint warning.
 *
 * Registering them under `font-size` is what makes the three intended behaviours hold: a type
 * utility and a colour utility both survive, two type utilities collapse last-wins, and a stock
 * `text-sm` still overrides `text-body-md` (they genuinely do conflict).
 */
const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: [...TYPE_UTILITIES] }] } },
});

/**
 * Merge Tailwind class lists so the LAST conflicting utility wins.
 *
 * Plain `classNames` keeps both `px-4` and `px-8`; which one applies then depends on CSS source
 * order, not on the caller's intent. `twMerge` drops the earlier one, which is what makes a
 * `className` prop able to override a recipe's own utilities.
 */
export const cn = (...inputs: ArgumentArray) => twMerge(classNames(inputs));
